
import React, { useState } from "react";
import { CheckCircle, X } from "lucide-react";
function EligibilityCardsSection() {
  const [openModal, setOpenModal] = useState(null);

  const cards = [
    {
      id: 'who-can-apply',
      title: 'Who Can Apply',
      content: (
        <p className="text-gray-700 leading-relaxed">
          Any group of individuals with a common economic or social objective
          may apply to form a cooperative society. The proposed society should
          function on cooperative principles and serve the collective interest
          of its members.
        </p>
      )
    },
    {
      id: 'eligibility',
      title: 'Types of Societies Eligible for Registration',
      content: (
        <ul className="space-y-3 text-gray-700">
          {[
            "Credit societies",
            "Agricultural and allied sector cooperatives",
            "Marketing and consumer cooperatives",
            "Housing and urban cooperatives",
            "Labour and workers cooperatives",
            "Industrial and service cooperatives",
            "Any other society that aligns with cooperative objectives",
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle className="text-blue-600 w-5 h-5 mt-1 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )
    }
  ];

  return (
    <div className="max-w-7xl  mx-auto mb-14">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map((card, index) => {
          // 🔽 ONLY CHANGE IS HERE
          const colors = [
            'bg-blue-600 border-blue-600 text-white',
            'bg-gray-600 border-gray-600 text-white'
          ];

          return (
            <div
              key={card.id}
              onClick={() => setOpenModal(card.id)}
              className={`${colors[index]} rounded-2xl shadow-lg border-2 p-8 cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1`}
            >
              {/* 🔽 text color adjusted only */}
              <h2 className="text-xl font-bold text-white">
                {card.title}
              </h2>
              <p className="text-sm text-white/80 mt-2">
                Click to view details
              </p>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {cards.find(c => c.id === openModal)?.title}
              </h2>
              <button
                onClick={() => setOpenModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {cards.find(c => c.id === openModal)?.content}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={() => setOpenModal(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Society() {
  return (
    <div className="w-full bg-white py-12 px-4 lg:px-20">
      {/* Header */}
      <div className="max-w-7xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900">
          Society Registration
        </h1>
      </div>

      {/* Overview */}
      <div className="max-w-7xl mx-auto bg-[#f1f7ff] rounded-2xl p-8 mb-14">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
        <div className="border-l-4 border-blue-600 pl-4 text-gray-700 leading-relaxed space-y-4">
          <p>
            Registration of a cooperative society is the first and foundational
            step in forming a legally recognised cooperative institution in Tamil
            Nadu. The Registrar of Cooperative Societies administers the
            registration process in accordance with the Tamil Nadu Cooperative
            Societies Act, 1983, ensuring that new cooperatives are established
            with clear objectives, democratic governance, and statutory
            compliance.
          </p>
          <p>
            Registered cooperative societies support a wide range of activities
            including credit, agriculture, marketing, housing, labour, and
            consumer services. They play an important role in promoting inclusive
            and sustainable development across the State.
          </p>
        </div>
      </div>

     {/* Who Can Apply - Card Section */}
      <EligibilityCardsSection />

{/* Process Flow – Full Screen Width */}
<div className="w-screen bg-gray-100 mb-20 py-20 relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
  
  <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
    Registration Process
  </h2>

  {/* Inner content max width */}
  <div className="max-w-7xl mx-auto px-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16 relative">

      {[
        { title: "Formation of Promoter Group", color: "bg-cyan-200" },
        { title: "Preparation of Documents", color: "bg-blue-300" },
        { title: "Submission of Application", color: "bg-purple-300" },

        { title: "Scrutiny & Verification", color: "bg-pink-300" },
        { title: "Clarifications (If Required)", color: "bg-orange-300" },
        { title: "Approval of Society", color: "bg-yellow-300" },

        { title: "Certificate Issued", color: "bg-orange-200" },
        { title: "Commencement of Operations", color: "bg-yellow-200" },
        { title: "Post-registration Compliance", color: "bg-green-300" },
      ].map((step, idx) => (
        <div
          key={idx}
          className={`relative rounded-2xl p-6 text-center shadow-md ${step.color}`}
        >
          {/* Step circle */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-white shadow rounded-full h-12 w-12 flex items-center justify-center text-lg font-bold">
            {idx + 1}
          </div>

          <p className="font-semibold text-gray-800 mt-6">
            {step.title}
          </p>
        </div>
      ))}
    </div>
  </div>
</div>

{/* Documents Required */}
<div className="w-full bg-blue-100 py-12">
  <div className="max-w-7xl mx-auto px-6 lg:px-20">
    <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
      Documents Required
    </h2>

    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-blue-900">
      {[
        "List of proposed members with identification details",
        "Draft bye-laws describing objectives, governance structure, capital, and operations",
        "Address proof of the registered office",
        "Details of membership subscriptions and share capital",
        "Any additional documents required for specific categories of societies",
      ].map((doc, idx) => (
        <li
          key={idx}
          className="flex items-start gap-3 p-4 bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-200"
        >
          <CheckCircle className="text-blue-600 w-6 h-6 mt-1 flex-shrink-0" />
          <span className="leading-snug text-sm md:text-base">{doc}</span>
        </li>
      ))}
    </ul>
  </div>
</div>

    </div>
  );
}
